import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Package,
  Building2,
  Download,
  FileSpreadsheet,
  Loader2,
} from "lucide-react";
import type { AssetExportFormat } from "@/features/exports/services";

interface ClientAssetsHeaderProps {
  clientName: string;
  logoUrl: string | null | undefined;
  totalAssets: number;
  propertiesCount: number;
  exportsEnabled: boolean;
  exportingFormat: AssetExportFormat | null;
  onExport: (format: AssetExportFormat) => void;
}

/** Header band: logo + title + asset/property badges + export buttons. */
export function ClientAssetsHeader({
  clientName,
  logoUrl,
  totalAssets,
  propertiesCount,
  exportsEnabled,
  exportingFormat,
  onExport,
}: ClientAssetsHeaderProps) {
  return (
    <div className="relative bg-gradient-to-r from-card via-card/98 to-accent/20 backdrop-blur-xl border-b border-border/50 flex-shrink-0 overflow-hidden">
      {/* Subtle decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 pointer-events-none" />

      <div className="h-23 px-6 flex items-center relative z-10">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-5">
            {/* Enhanced Logo with glow effect */}
            <div className="relative group">
              {logoUrl ? (
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <img
                    src={logoUrl}
                    alt={`${clientName} logo`}
                    className="relative w-14 h-14 rounded-xl object-cover border border-border/50 shadow-lg ring-1 ring-primary/10 transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-primary via-primary/90 to-primary/70 flex items-center justify-center border border-primary/20 shadow-lg ring-1 ring-primary/20 transition-transform duration-300 group-hover:scale-105">
                    <Package className="h-7 w-7 text-primary-foreground" />
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Title and Stats */}
            <div className="flex flex-col gap-1.5">
              <h1 className="text-2xl font-bold text-foreground tracking-tight bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
                Assets - {clientName}
              </h1>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 transition-colors"
                >
                  <Package className="h-3 w-3 mr-1" />
                  {totalAssets.toLocaleString()} Assets
                </Badge>
                <Badge
                  variant="secondary"
                  className="px-2 py-0.5 text-xs font-medium bg-accent/30 text-foreground border-accent/30 hover:bg-accent/40 transition-colors"
                >
                  <Building2 className="h-3 w-3 mr-1" />
                  {propertiesCount} Properties
                </Badge>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {exportsEnabled && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExport("xlsx")}
                  disabled={exportingFormat !== null}
                  className="gap-2"
                >
                  {exportingFormat === "xlsx" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4" />
                  )}
                  Export as XLSX
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExport("csv")}
                  disabled={exportingFormat !== null}
                  className="gap-2"
                >
                  {exportingFormat === "csv" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Export as CSV
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
